import { isEmpty } from "lodash";
import { BenefitPlanDetails, CheckEligibility, EligibilityCheck, Patient, PatientInformation, PatientInformationView } from "../models/Patient";
import { ReferralDetail } from "../models/Referral";
import { Constants, getContractIdPbp } from "./Referral";
import { getGender, getLanguage, getValidMBI, getValue, getZipCode, mapUserDefinedFields } from "../utils";
import { formatDate } from "../utils/DateTime";
import { DateFormats } from "./Date";

export enum EligibilityCheckActionDispatchTypes {
  ELIGIBILITY_CHECK_SUCCESS = "ELIGIBILITY_CHECK_SUCCESS",
  ELIGIBILITY_CHECK_ERROR = "ELIGIBILITY_CHECK_ERROR",
  ELIGIBILITY_CHECK_INVOKE = "ELIGIBILITY_CHECK_INVOKE",
  ELIGIBILITY_CHECK_RESET = "ELIGIBILITY_CHECK_RESET",
}

export enum PatientFromMbiActionDispatchTypes {
  PATIENT_FROM_MBI_SUCCESS = "PATIENT_FROM_MBI_SUCCESS",
  PATIENT_FROM_MBI_ERROR = "PATIENT_FROM_MBI_ERROR",
  PATIENT_FROM_MBI_INVOKE = "PATIENT_FROM_MBI_INVOKE",
  PATIENT_FROM_MBI_RESET = "PATIENT_FROM_MBI_RESET",
}

export enum InformationActionDispatchTypes {
  INFORMATION_SUCCESS = "INFORMATION_SUCCESS",
  INFORMATION_INVOKE = "INFORMATION_INVOKE",
  INFORMATION_ERROR = "INFORMATION_ERROR",
  INFORMATION_RESET = "INFORMATION_RESET",
}

export enum PatientInformationByIdActionDispatchTypes {
  PATIENT_INFORMATION_BY_ID_SUCCESS = "PATIENT_INFORMATION_BY_ID_SUCCESS",
  PATIENT_INFORMATION_BY_ID_ERROR = "PATIENT_INFORMATION_BY_ID_ERROR",
  PATIENT_INFORMATION_BY_ID_INVOKE = "PATIENT_INFORMATION_BY_ID_INVOKE",
  PATIENT_INFORMATION_BY_ID_RESET = "PATIENT_INFORMATION_BY_ID_RESET",
}

export enum IcdCodeActionDispatchTypes {
  ICD_CODE_SUCCESS = "ICD_CODE_SUCCESS",
  ICD_CODE_INVOKE = "ICD_CODE_INVOKE",
  ICD_CODE_RESET = "ICD_CODE_RESET",
  ICD_CODE_ERROR = "ICD_CODE_ERROR",
}

export enum ReAuthDefaultValueActionDispatchTypes {
  RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_SUCCESS = "RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_SUCCESS",
  RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_INVOKE = "RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_INVOKE",
  RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_ERRORS = "RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_ERRORS",
  RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_RESET = "RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_RESET",
}

export enum BenefitPlanUidActionDispatchTypes {
  BENEFIT_PLAN_UID_SUCCESS = "BENEFIT_PLAN_UID_SUCCESS",
  BENEFIT_PLAN_UID_INVOKE = "BENEFIT_PLAN_UID_INVOKE",
  BENEFIT_PLAN_UID_ERRORS = "BENEFIT_PLAN_UID_ERRORS",
  BENEFIT_PLAN_UID_RESET = "BENEFIT_PLAN_UID_RESET"
}

export enum CreatePatientActionDispatchTypes {
  CREATE_PATIENT_SUCCESS = "CREATE_PATIENT_SUCCESS",
  CREATE_PATIENT_INVOKE = "CREATE_PATIENT_INVOKE",
  CREATE_PATIENT_ERRORS = "CREATE_PATIENT_ERRORS",
  CREATE_PATIENT_RESET = "CREATE_PATIENT_RESET"
}

export enum LogContractIdPBPDetailsActionDispatchTypes {
  LOG_CONTRACT_ID_PBP_DETAILS_SUCCESS = "LOG_CONTRACT_ID_PBP_DETAILS_SUCCESS",
  LOG_CONTRACT_ID_PBP_DETAILS_INVOKE = "LOG_CONTRACT_ID_PBP_DETAILS_INVOKE",
  LOG_CONTRACT_ID_PBP_DETAILS_ERRORS = "LOG_CONTRACT_ID_PBP_DETAILS_ERRORS",
  LOG_CONTRACT_ID_PBP_DETAILS_RESET = "LOG_CONTRACT_ID_PBP_DETAILS_RESET"
}

export const HealthPlanData = {
  label: "",
  value: "",
};

export const GenderType = [
  {
    label: "Male",
    value: "M",
  },
  {
    label: "Female",
    value: "F",
  },
  {
    label: "Unknown",
    value: "U",
  },
];

export const InformationResponseDefaultValues: PatientInformationView = {
  patientId: 0,
  referralId: 0,
  serviceRequestId: 0,
  patientName: "",
  patientFirstName: null,
  patientLastName: null,
  languagePreferenceValue: null,
  isPriorAuthIntakeStageComplete: true,
  isReAuthIntakeStageComplete: true,
  isPrioAuthCompleted: true,
  isManual: true,
  blobId: 0,
  blobFileName: "",
  patientEligibilityId: 0,
  intakeStageId: 0,
  eligibilityPayerId: "",
  eligibilityPayerName: "",
  ahcccsId: "",
  memberInfo: {
    dateOfBirth: "",
    gender: null,
    subscriberId: null,
    medicareBeneficiaryIdentifier: null,
    phoneNo: null,
    healthPlan: null,
    effectiveDate: "",
    effectiveThrough: "",
    dateOfIssuance: "",
    benefitPlan: null,
    product: null,
    insuranceState: null,
    cellPhone: null,
    accommodationNames: null,
    mobilePhone: null,
    alternatePhone: null,
    contractIdPbp: null,
    languageId: null,
    languageName: null,
    extension: null,
    accommodationId: null,
    groupPlanId: null,
    relationshipTypeId: null,
    subscriberFirstName: null,
    subscriberLastName: null,
    subscriberDateOfBirth: null,
    subscriberGender: null,
  },
  addressInfo: {
    patientMailingAddress: {
      streetName1: null,
      streetName2: null,
      zipCode: null,
      city: null,
      patientState: null,
      addressTypeId: 0,
      county: null,
      country: null,
      latitude: 0,
      longitude: 0,
    },
    patientResidentialAddress: {
      streetName1: null,
      streetName2: null,
      zipCode: null,
      city: null,
      patientState: null,
      addressTypeId: 0,
      county: null,
      country: null,
      latitude: 0,
      longitude: 0,
    },
    residentialAddress: "",
    mailingAddress: "",
  },
  emergencyContact: [],
  primaryPhysician: {
    referralId: 0,
    referralPhysicianDetailId: 0,
    physicianId: 0,
    physicianName: "",
    physicianNpiId: "",
    phoneExt: null,
    phoneNo: "",
    taxId: null,
    streetName1: "",
    streetName2: null,
    fax: null,
    county: "",
    city: "",
    physicianState: "",
    zipCode: "",
    physicianTypeId: 0,
    physicianAddress: null,
    physicianUid: "",
    fullNameCreated: "",
  },
  isPriorAuthCreated: false,
  authorizationTypeId: 0,
};

export const allowedTypes = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/plain",
];

export const allowedTypesForNOMNC = [
  "application/pdf",
  "image/png",
  "image/jpeg",
];

export const DocumentError1 = "Total file size exceeds the 75 MB limit.";
export const DocumentError2 = "File type not allowed:";

export enum TextConstants {
  HCSC = "HCSC",
  AMERIHEALTH_CARITAS_KEYSTONE = "AmeriHealth Caritas/Keystone",
  PAYER_NAME = "payerName",
  HOME_HEALTH_CARE = "HOME HEALTH CARE NEW REFERRAL/PRIOR AUTHORIZATION FORM",
  HOME_HEALTH_CARE_RE_AUTH = "HOME HEALTH CARE REQUEST RE-AUTHORIZATION FORM",
  AUTH_SENTENCE = "Authorizations will only be backdated up to 72 hours from date of receipt.",
  ELIGIBILITY_CHECK = "Please check for eligibility before filling out prior authorization form.",
  HCSC_CHECK = " HCSC currently has 2 formats for their SubscriberIDs. If you receive a message stating that the member searched was not found, it is tango's recommendation to add a 01 to the end of the SubscriberID and attempt to search for your member a second time. If the member continues not to be found, you may proceed manually",
  BLUE_CROSS_BCBS_TN_MICHIGAN_CHECK = "Please do not include the three character prefix with the Subscriber ID when searching for a member",
  AMERIHEALTH_CARITAS_CHECK = ` tango and AmeriHealth Caritas/Keystone are in the process of loading membership files into the tango system. Please complete all required fields below and select the "Enter Request Manually" button to proceed.`,
  SKILLED_DICIPLINE = "Which Skilled Disciplines are Ordered for Start of Care?",
  ORDERING_SAME_AS_PRIMARY = "Is the ordering physician same as primary care physician entered above?",
  SKIP_THIS_STEP = "Skip this Step",
  MEMBER_NOT_FOUND = "Member Not Matched. Please confirm member data and attempt again.",
  PHYSICIAN_VERBIAGE = "Please enter physician detail for member, primary care physician is not mandatory, but is encouraged if the data is available to you. Ordering physician must be entered to proceed.",
  MEMBER_INFO1 = " If you continue to have issue verifying the member and believe this member requires a tango authorization, please complete the member information fields below.",
  MEMBER_INFO2 = " Click 'Enter request manually' to continue to the authorization form.",
  MEMBER_INFO3 = " Complete the authorization form fields with required information and click 'Submit'.",
  MEMBER_CONFIRM = "Once submitted, your authorization will be sent to our front-end teams to verify eligibility and process your request.",
  MEMBER_CONTINUE = "If you continue to have issues submitting this form, please submit your request via our website under the Uploads Center @ ",
  RE_AUTH_QUESTION = "Questions? Call (888) 705-5274",
  RE_AUTH_INFO = `If you are submitting additional documents in regards to a previous re-authorization request, please complete the required fields and scroll down to the Additional Information section.`,
  RE_AUTH_VISIT_HEADER = "Please request further visits below:",
  RE_AUTH_CURRENT_AUTH = "Current Authorization Date Range:",
  RE_AUTH_EXTEND_AUTH = "Extend Authorization to Next Date Range:",
  RE_AUTH_FOOTER = "RA notification to agency confirms receipt of determination with approved visits. Should you disagree with approval, please notify tango UM Department at (888) 705-5274. Should a material change in member status occur, submit an additional request with pertinent clinical documentation.",
  AUTH_TRACKER = "Auth Tracker",
  PATIENT_LIST = "Patient List",
  ELIGIBLE = "The member is eligible.",
  NOT_ELIGIBLE = "The member is not eligible.",
  CHECK_ELIGIBILITY_TAB_MESSAGE = "On this tab, you can seamlessly verify eligibility for your member. To request an authorization, or re-authorization, please submit your request through the appropriate authorization process.",
  DISCIPLINE_WARNING = "AZ Complete Medicaid members do not have speech therapy coverage after their 21st birthday. This request has dates in which the member will turn 21 years old, please schedule visits accordingly",
  BCBS_TN_WARNING = "Home Health Aide and Medical Social Work visits do not require prior authorization for BCBS TN membership; however you may proceed to request those disciplines.",
  SPEECH_THERAPY = "Speech Therapy",
  MEDICAID = "MEDICAID",
  ST = 'ST',
  MSW = "MSW",
  TRUE = "True",
  MALE = "Male",
  FEMALE = "Female",
  CONTRACTID_PBP_MESSAGE = "The health plan does not require home health prior authorization for this member. You do not need to submit an authorization, but you may proceed if you prefer.",
  REFERRAL_NOT_ELIGIBLE = "Referral with below details couldn't be verified / Not Eligible",
  IF_THE_MEMBERS_ELIGIBILITY = "If the member's eligibility",
}

export const supportedFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/png",
];

export enum MemberInfoDataActionDispatchTypes {
  MEMBER_INFO_DATA_SUCCESS = "MEMBER_INFO_DATA_SUCCESS",
  MEMBER_INFO_DATA_INVOKE = "MEMBER_INFO_DATA_INVOKE",
  MEMBER_INFO_DATA_RESET = "MEMBER_INFO_DATA_RESET",
}

export const subscriberDetails = (checkEligibilityState: CheckEligibility) =>
  mapUserDefinedFields({
    healthPlanUid: getValue(
      checkEligibilityState,
      "additionalNotes.healthPlanUid"
    ),
    UserDefined7: getValue(
      checkEligibilityState,
      "supplementalInformation.info[0].userDefined7"
    ),
    UserDefined6: getValue(
      checkEligibilityState,
      "supplementalInformation.info[0].userDefined6"
    ),
  });

interface InformationPriorAuthParams {
  checkEligibilityState: CheckEligibility;
  patientEligibilityStatus: string;
  eligibilityPayload: EligibilityCheck | null | undefined;
  patientFromMbiState?: Patient | null;
  reAuthDefaultValueState?: ReferralDetail | null;
  benefitPlanUidState?: BenefitPlanDetails;
  patientInformationByIdState?: PatientInformation;
}

export const getPayloadForInformationPriorAuth = ({
  checkEligibilityState,
  patientEligibilityStatus,
  eligibilityPayload,
  patientFromMbiState,
  reAuthDefaultValueState,
  benefitPlanUidState,
  patientInformationByIdState,
}: InformationPriorAuthParams) => {
  const patientIdFromMbi = patientFromMbiState?.patientDetails && !isEmpty(patientFromMbiState?.patientDetails[0]) &&  patientFromMbiState?.patientDetails[0].patientId > 0 
    ? patientFromMbiState?.patientDetails[0].patientId 
    : 0;
  let patientId = 0;
  if (reAuthDefaultValueState) {
    patientId = reAuthDefaultValueState.patientId;
  } else if (patientIdFromMbi > 0) {
    patientId = patientIdFromMbi;
  } else {
    patientId = patientInformationByIdState?.patientId ?? 0;
  }
  const { subscriberSuffix, groupPlanId, relationshipTypeId } = subscriberDetails(checkEligibilityState);

  const informationReceived = {
    patientId: patientId,
    healthPlanUid: checkEligibilityState?.additionalNotes?.healthPlanUid,
    product: benefitPlanUidState?.product,
    benefitPlanUid: checkEligibilityState?.additionalNotes?.benefitPlanUid,
    medicareBeneficiaryIdentifier:
      getValidMBI(checkEligibilityState?.supplementalInformation?.info[0]?.mbi),
    ahcccsId: null,
    eligibilityStatus: patientEligibilityStatus,
    effectiveDate:
      checkEligibilityState?.supplementalInformation?.info[0]?.healthCoverage
        ?.beginDate,
    effectiveThrough:
      checkEligibilityState?.supplementalInformation?.info[0]?.healthCoverage
        ?.endDate,
    subscriberId: checkEligibilityState?.additionalNotes?.scrubbedSubscriberID,
    eligibilityPayerId: getValue(eligibilityPayload, "payerId"),
    eligibilityPayerName: getValue(eligibilityPayload, "payerName"),
    apiEligibilityId: checkEligibilityState?.additionalNotes?.apiRequestId,
    isManual: false,
    alternatePhone: null,
    healthPlan: "",
    benefitPlanName: "",
    healthPlanState: "",
    accommodationNames: checkEligibilityState?.supplementalInformation?.info[0]?.userDefined5,
    contractIdPbp: getContractIdPbp(checkEligibilityState?.supplementalInformation?.info[0]?.userDefined3,
      checkEligibilityState?.supplementalInformation?.info[0]?.userDefined4),
    languagePreferenceValue: getLanguage(checkEligibilityState?.supplementalInformation?.info[0]?.userDefined1.toUpperCase()),
    gender: getGender(getValue(patientInformationByIdState, 'memberInfo.gender')) === "" ? getGender(getValue(checkEligibilityState, "record.subscriberGender")) : getGender(getValue(patientInformationByIdState, 'memberInfo.gender')),
    phoneNo: getValue(patientInformationByIdState, 'memberInfo.phoneNo'),
    subscriberSuffix,
    groupPlanId,
    subscriberPrefix: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined8", ""),
    relationshipTypeId,
    subscriberFirstName: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined9", ""),
    subscriberLastName: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined10", ""),
    subscriberDateOfBirth: formatDate(
                  getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined11"),
                  DateFormats.YYYY_MM_DD,
                  DateFormats.YYYYMMDD
                ) || "",
    subscriberGender: getGender(getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined12", "")),
  };

  const residentialAddress =
    getValue(
      checkEligibilityState,
      "supplementalInformation.info[0].addresses",
      []
    ).find((item: any) => item?.addressType?.toLowerCase() === "residential") || {};

  const patientResidentialAddress = {
    streetName1: getValue(residentialAddress, "addressLine", null),
    streetName2: getValue(residentialAddress, "addressLine2", null),
    county: getValue(residentialAddress, "county", null),
    city: getValue(residentialAddress, "city", null),
    patientState: getValue(residentialAddress, "state", null),
    zipCode: getZipCode(getValue(residentialAddress, "zipCode", null)),
    latitude: null,
    longitude: null,
    country: getValue(residentialAddress, "countryCode", null),
    addressTypeId: 1,
  };

  const mailingAddress =
    getValue(
      checkEligibilityState,
      "supplementalInformation.info[0].addresses",
      []
    ).find((item: any) => item?.addressType?.toLowerCase() === "mailing") || {};
    
  const patientMailingAddress = {
    streetName1: getValue(mailingAddress, "addressLine", null),
    streetName2: getValue(mailingAddress, "addressLine2", null),
    county: getValue(mailingAddress, "county", null),
    city: getValue(mailingAddress, "city", null),
    patientState: getValue(mailingAddress, "state", null),
    zipCode: getZipCode(getValue(mailingAddress, "zipCode", null)),
    latitude: null,
    longitude: null,
    country: getValue(mailingAddress, "countryCode", null),
    addressTypeId: 3,
  };
  const informationPayload = {
    informationReceived: informationReceived,
    patientMailingAddress:
      isEmpty(patientMailingAddress?.zipCode) ||
      isEmpty(patientMailingAddress?.patientState)
        ? { ...patientResidentialAddress, addressTypeId: 3 }
        : patientMailingAddress,
    patientResidentialAddress:
      isEmpty(patientResidentialAddress?.zipCode) ||
      isEmpty(patientResidentialAddress?.patientState)
        ? { ...patientMailingAddress, addressTypeId: 1 }
        : patientResidentialAddress,
  };

  return informationPayload;
};

export const ApprovedAuthType = [
  {
    label: Constants.EMAIL,
    value: 1,
  },
  {
    label: Constants.FAX,
    value: 2,
  },
];